import * as React from 'react';
import { StyleSheet } from 'aphrodite/no-important';
import { TYPOGRAPHY_SIZE } from 'mm-theme-configuration/dist/consts';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { HeaderLinksDataProps } from '../Header';
import { TextLinkComponent, generateLinkStyleFromTheme } from '../../../../partials/link/TextLink';
import { Span, semanticCSS } from '../../../../../typography/semanticTags';
import { notActiveLinkIdleColor } from '../header.utils';
import { BaseUnit, baseUnit } from '../../../../../theming/baseUnitDefinition';
import { TranslatedFontSize } from '../../../../../theming/fontSizeTranslator';
import { getStyleBySize } from '../../../../../typography/semanticTags/semanticTags.utils';
import { useTheme } from '../../../../../theming/useTheme';

const componentConfigThemeKey = 'secondaryLink';

interface SecondaryLinkDataProps {
  linksLength: number;
  index: number;
  isHoveredLink: boolean;
  pagePath: string;
  linkData: HeaderLinksDataProps;
  maxVisibleLinks: number;
}

interface SecondaryLinkThemeProps {
  baseUnit: BaseUnit;
  hoverOtherLinksColors: string;
  idleColor: string;
  activeStateColor: string;
  hoverStateColor: string;
  fontSizesStyles: TranslatedFontSize;
}

const getTextStyle = (isLastLink: boolean) => {
  return StyleSheet.create({
    link: {
      width: 'max-content',
      [MEDIA_BREAKPOINTS.medium]: {
        paddingRight: isLastLink ? `${baseUnit * 2}px` : 0,
      },
      [MEDIA_BREAKPOINTS.small]: {
        paddingRight: isLastLink ? `${baseUnit * 2}px` : 0,
      },
    },
  });
};

export const isLastLink = (index: number, linksLength: number) => index + 1 === linksLength;

const getContentStyle = (isActiveLink: boolean, fontSizesStyles: TranslatedFontSize) => {
  const fontSizesActive = { fontSizeSmall: TYPOGRAPHY_SIZE.BIGGER, fontSizeMedium: TYPOGRAPHY_SIZE.BIGGER, fontSizeLarge: TYPOGRAPHY_SIZE.BIGGER };
  const fontSizesNotActive = { fontSizeSmall: TYPOGRAPHY_SIZE.NORMAL, fontSizeMedium: TYPOGRAPHY_SIZE.NORMAL, fontSizeLarge: TYPOGRAPHY_SIZE.NORMAL };
  return isActiveLink ? getStyleBySize(fontSizesStyles, fontSizesActive) : getStyleBySize(fontSizesStyles, fontSizesNotActive);
};

export const SecondaryLink: React.FunctionComponent<SecondaryLinkDataProps> = props => {
  const { linksLength, index, isHoveredLink, pagePath, linkData, maxVisibleLinks = 6 } = props;
  const { activeStateColor, hoverOtherLinksColors, idleColor, hoverStateColor, fontSizesStyles } = useTheme<SecondaryLinkThemeProps>(componentConfigThemeKey);
  const isActiveLink = linkData.href === pagePath;
  const calculatedIdleColor = isActiveLink ? activeStateColor : notActiveLinkIdleColor(isHoveredLink, hoverOtherLinksColors, idleColor);
  const calculatedHoverColor = isActiveLink ? activeStateColor : hoverStateColor;

  const linkStyleBasedOnScreen = linksLength > maxVisibleLinks && index >= maxVisibleLinks - 1
    ? {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'inline-flex',
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'inline-flex',
      },
    }
    : {};

  const linkStyle = generateLinkStyleFromTheme({ idleColor: calculatedIdleColor, activeStateColor, hoverStateColor: calculatedHoverColor }, linkStyleBasedOnScreen);
  const style = getTextStyle(isLastLink(index, linksLength));
  const contentStyle = getContentStyle(isActiveLink, fontSizesStyles);

  return (
    <TextLinkComponent href={linkData.href} linkStyle={linkStyle}>
      <Span styles={semanticCSS(contentStyle, style.link)}>{linkData.text}</Span>
    </TextLinkComponent>
  );
};
